/**
 * Personalization - rank interventions by how much *expected* relief they
 * provide given the user's current pattern intensities.
 *
 * Score model (interim, before empirical data):
 *
 *   score(intervention) =
 *     Σ over (tag, weight) in contributes_to_tags:
 *       relief_signal(tag) * |weight|
 *
 *   where relief_signal(tag) =
 *     - shadow tag → tag.percent / 100  (high intensity → big payoff to reduce)
 *     - engine tag → (100 - tag.percent) / 100 (low intensity → big payoff to build)
 *
 * Bonuses:
 *   + 0.10 if intervention.primary_dimension === user.weakest dimension
 *   + 0.05 if any contributed-to tag is in the user's top-3 pendulum-driving tags
 *
 * The intent: surface techniques that match where the user is leaking the most,
 * not just walk a sequential roadmap. Used by `roadmap.ts` to order tasks
 * inside a phase and to bias daily-focus selection.
 */

import { ScoringResult } from './scoringEngine';
import { Intervention } from './contentLoader';

export interface RankedIntervention {
  intervention: Intervention;
  score: number;
  reasons: string[];
}

const DIMENSION_BONUS = 0.10;
const PENDULUM_TAG_BONUS = 0.05;

function reliefSignal(percent: number, polarity: 'shadow' | 'engine'): number {
  return polarity === 'engine' ? (100 - percent) / 100 : percent / 100;
}

export function rankInterventions(
  interventions: Intervention[],
  scores: ScoringResult,
  opts?: { weakestDimensionId?: string; topPendulumTagIds?: string[] },
): RankedIntervention[] {
  const tagsById = new Map(scores.tags.map((t) => [t.id, t]));
  const weakDim = opts?.weakestDimensionId;
  const topPendTagSet = new Set(opts?.topPendulumTagIds ?? []);

  return interventions
    .map((iv) => {
      const reasons: string[] = [];
      let score = 0;

      Object.entries(iv.contributes_to_tags).forEach(([tagId, w]) => {
        const tag = tagsById.get(tagId);
        if (!tag) return;
        const signal = reliefSignal(tag.percent, tag.type);
        const contribution = signal * Math.abs(w);
        score += contribution;
        if (contribution >= 0.10) {
          reasons.push(`${tag.id} ${tag.percent}%`);
        }
        if (topPendTagSet.has(tagId)) {
          score += PENDULUM_TAG_BONUS * Math.abs(w);
        }
      });

      if (weakDim && iv.primary_dimension === weakDim) {
        score += DIMENSION_BONUS;
        reasons.unshift(`weakest dim: ${weakDim}`);
      }

      return { intervention: iv, score: Math.round(score * 1000) / 1000, reasons };
    })
    .sort((a, b) => b.score - a.score);
}

/**
 * Pull the tag ids that drive the user's strongest pendulum (top by amplitude).
 * Used as a `topPendulumTagIds` hint for ranking. Pulls from the inverted
 * pendulum_axes weights stored on the unified content.
 */
export function topPendulumTagIds(
  scores: ScoringResult,
  axes: Record<string, { weights: Record<string, Record<string, number>> }>,
  limit = 3,
): string[] {
  const top = [...scores.pendulums].sort((a, b) => b.amplitude - a.amplitude)[0];
  if (!top) return [];
  const axis = axes[top.id];
  if (!axis) return [];
  const tagToWeight: Record<string, number> = {};
  Object.values(axis.weights).forEach((poleMap) => {
    Object.entries(poleMap).forEach(([tag, w]) => {
      tagToWeight[tag] = Math.max(tagToWeight[tag] || 0, w);
    });
  });
  return Object.entries(tagToWeight)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag]) => tag);
}
